import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EChartsOption } from 'echarts';
import type { ECharts } from 'echarts';
import { RecordData } from 'src/app/types';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import { MeasurementEvent, ParsedMeasurement } from '../../models/parsed-measurement.model';
import { NeckOrientation } from '../../services/neck-angle.service';

import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import { ObservationChipsComponent } from '../shared/observation-chips/observation-chips.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';

@Component({
  selector: 'app-protocol-monitoring',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    ObservationChipsComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-monitoring.component.html',
  styleUrl: './protocol-monitoring.component.scss',
})
export class ProtocolMonitoringComponent implements OnDestroy {
  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);
  readonly beforeMealImage = input<string | null>(null);
  readonly afterMealImage = input<string | null>(null);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(0);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) return;
      this.waveHostReady.emit(comp);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
  }

  // ── Computed: header ──
  protected readonly headerTitle = computed(() => {
    const rt = this.recordData()?.data?.recordType as string | undefined;
    switch (rt) {
      case 'MEAL_MONITORING_WITH_VITALS': return $localize`:@@details.protocolMonitoring.headerTitle.mealWithVitals:食事モニタリング（バイタル）`;
      case 'MEAL_MONITORING': return $localize`:@@details.protocolMonitoring.headerTitle.meal:食事モニタリング`;
      case 'MONITORING': return $localize`:@@details.protocolMonitoring.headerTitle.monitoring:モニタリング`;
      default: return $localize`:@@details.protocolMonitoring.headerTitle.default:食事モニタリング`;
    }
  });

  protected readonly patientName = computed(() =>
    this.recordData()?.data?.userName ?? '',
  );

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });

  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) return $localize`:@@details.protocolMonitoring.measurer:計測者: ${d.recorderName}`;
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) return $localize`:@@details.protocolMonitoring.measurer:計測者: ${(m.lastName ?? '') + (m.firstName ?? '')}`;
    return '';
  });

  // ── Computed: observation KPI ──
  protected readonly swallowCount = computed(() => {
    const kpi = this.parsedMeasurement()?.vsKpi;
    if (kpi) return kpi.swallowCount;
    const d = this.recordData()?.data;
    return d?.numOfSwallowings ?? 0;
  });

  protected readonly coughCount = computed(() => {
    const kpi = this.parsedMeasurement()?.vsKpi;
    if (kpi) return kpi.coughCount;
    const d = this.recordData()?.data;
    return d?.numOfCoughs ?? 0;
  });

  protected readonly durationSec = computed(() => {
    const kpi = this.parsedMeasurement()?.vsKpi;
    if (kpi) return kpi.measurementDurationSec;
    return 0;
  });

  // ── Monitoring pictures ──
  protected readonly beforeImageFailed = signal(false);
  protected readonly afterImageFailed = signal(false);

  protected readonly hasPictures = computed(() =>
    !!(this.beforeMealImage() || this.afterMealImage()),
  );

  protected onImageError(type: 'before' | 'after'): void {
    if (type === 'before') this.beforeImageFailed.set(true);
    else this.afterImageFailed.set(true);
  }

  // ── Computed: events, footer ──
  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly dataId = computed(() => this.recordData()?.id ?? '');

  protected readonly downloadUrl = computed(() =>
    this.recordData()?.data?.storage?.csvUrl ?? '',
  );

  protected readonly disclaimer = $localize`:@@details.protocolMonitoring.disclaimer:食事モニタリングは観察記録です。嚥下・咳嗽の自動識別にはAI推定値を含みます。結果は専門医の判断と併せてご利用ください。`;

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }

}
